/**
 * WI Community & Press
 * Save this content as: assets/wi-community-press.js
 */

(() => {
  const ROOT_SELECTOR = '[data-wi-community-press]';

  class WICommunityPress {
    constructor(root) {
      if (!root || root.dataset.wiCpInitialized === 'true') return;

      this.root = root;
      this.root.dataset.wiCpInitialized = 'true';
      this.mediaSlides = Array.from(root.querySelectorAll('[data-wi-cp-media]'));
      this.cards = Array.from(root.querySelectorAll('[data-wi-cp-card]'));
      this.prevButton = root.querySelector('[data-wi-cp-prev]');
      this.nextButton = root.querySelector('[data-wi-cp-next]');
      this.progress = root.querySelector('[data-wi-cp-progress]');
      this.progressBar = root.querySelector('[data-wi-cp-progress-bar]');
      this.currentCounters = Array.from(root.querySelectorAll('[data-wi-cp-current]'));
      this.status = root.querySelector('[data-wi-cp-status]');
      this.swipeAreas = Array.from(root.querySelectorAll('[data-wi-cp-swipe]'));
      this.count = Math.max(this.mediaSlides.length, this.cards.length);
      this.index = 0;
      this.loop = root.dataset.loop === 'true';
      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.pauseOnHover = root.dataset.pauseHover === 'true';
      this.autoplaySpeed = Math.max(Number(root.dataset.autoplaySpeed) || 5000, 2000);
      this.timer = null;
      this.startX = 0;
      this.startY = 0;
      this.pointerActive = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.count < 1) return;

      this.onPrev = () => this.goTo(this.index - 1, true);
      this.onNext = () => this.goTo(this.index + 1, true);
      this.onKeyDown = (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index - 1, true);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.index + 1, true);
        }
      };
      this.onPointerDown = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.pointerActive = true;
      };
      this.onPointerUp = (event) => {
        if (!this.pointerActive) return;
        this.pointerActive = false;
        const deltaX = event.clientX - this.startX;
        const deltaY = event.clientY - this.startY;
        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        if (deltaX < 0) this.goTo(this.index + 1, true);
        else this.goTo(this.index - 1, true);
      };
      this.onPointerCancel = () => {
        this.pointerActive = false;
      };
      this.onMouseEnter = () => this.stopAutoplay();
      this.onMouseLeave = () => this.startAutoplay();
      this.onFocusIn = () => this.stopAutoplay();
      this.onFocusOut = (event) => {
        if (!this.root.contains(event.relatedTarget)) this.startAutoplay();
      };

      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);
      this.swipeAreas.forEach((area) => {
        area.addEventListener('keydown', this.onKeyDown);
        area.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        area.addEventListener('pointerup', this.onPointerUp, { passive: true });
        area.addEventListener('pointercancel', this.onPointerCancel, { passive: true });
      });

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', this.onMouseEnter);
        this.root.addEventListener('mouseleave', this.onMouseLeave);
        this.root.addEventListener('focusin', this.onFocusIn);
        this.root.addEventListener('focusout', this.onFocusOut);
      }

      this.update(false);
      this.startAutoplay();
      this.root.wiCommunityPress = this;
    }

    normaliseIndex(nextIndex) {
      if (this.count <= 1) return 0;
      if (this.loop) return (nextIndex + this.count) % this.count;
      return Math.min(Math.max(nextIndex, 0), this.count - 1);
    }

    goTo(nextIndex, userInitiated = false) {
      const normalised = this.normaliseIndex(nextIndex);
      if (normalised === this.index && this.count > 1) {
        if (userInitiated) this.restartAutoplay();
        return;
      }

      this.index = normalised;
      this.update(true);
      if (userInitiated) this.restartAutoplay();
    }

    update(announce = true) {
      const nextIndex = this.count > 1 ? (this.index + 1) % this.count : this.index;

      this.mediaSlides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a, button, input, select, textarea, video[controls]').forEach((element) => {
          if (active) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', '-1');
        });

        slide.querySelectorAll('video').forEach((video) => {
          if (!active) {
            video.pause();
            return;
          }
          if (video.autoplay) {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
          }
        });
      });

      this.cards.forEach((card, cardIndex) => {
        const isCurrent = cardIndex === this.index;
        const isNext = this.count > 1 && cardIndex === nextIndex;
        card.classList.toggle('is-current', isCurrent);
        card.classList.toggle('is-next', isNext && !isCurrent);
        card.setAttribute('aria-hidden', String(!isCurrent && !isNext));

        card.querySelectorAll('a, button, input, select, textarea').forEach((element) => {
          if (isCurrent || isNext) element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', '-1');
        });
      });

      this.currentCounters.forEach((counter) => {
        counter.textContent = String(this.index + 1);
      });

      if (this.progress) {
        this.progress.setAttribute('aria-valuenow', String(this.index + 1));
      }
      if (this.progressBar) {
        this.progressBar.style.transform = `scaleX(${(this.index + 1) / this.count})`;
      }

      if (this.prevButton) {
        this.prevButton.disabled = this.count < 2 || (!this.loop && this.index === 0);
      }
      if (this.nextButton) {
        this.nextButton.disabled = this.count < 2 || (!this.loop && this.index === this.count - 1);
      }

      if (announce && this.status) {
        const activeCard = this.cards[this.index];
        const articleTitle = activeCard?.querySelector('.wi-cp__article-title')?.textContent?.trim();
        this.status.textContent = articleTitle
          ? `Slide ${this.index + 1} of ${this.count}: ${articleTitle}`
          : `Slide ${this.index + 1} of ${this.count}`;
      }
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.reducedMotion || this.count < 2 || this.timer) return;
      this.timer = window.setInterval(() => {
        if (!this.loop && this.index >= this.count - 1) {
          this.stopAutoplay();
          return;
        }
        this.goTo(this.index + 1, false);
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    destroy() {
      this.stopAutoplay();
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.swipeAreas.forEach((area) => {
        area.removeEventListener('keydown', this.onKeyDown);
        area.removeEventListener('pointerdown', this.onPointerDown);
        area.removeEventListener('pointerup', this.onPointerUp);
        area.removeEventListener('pointercancel', this.onPointerCancel);
      });
      this.root.removeEventListener('mouseenter', this.onMouseEnter);
      this.root.removeEventListener('mouseleave', this.onMouseLeave);
      this.root.removeEventListener('focusin', this.onFocusIn);
      this.root.removeEventListener('focusout', this.onFocusOut);
      delete this.root.wiCommunityPress;
      delete this.root.dataset.wiCpInitialized;
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => new WICommunityPress(root));
  };

  initialise(document);

  if (!window.wiCommunityPressEditorEventsBound) {
    window.wiCommunityPressEditorEventsBound = true;

    document.addEventListener('shopify:section:load', (event) => initialise(event.target));

    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target.querySelector(ROOT_SELECTOR);
      root?.wiCommunityPress?.destroy();
    });

    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target.closest('[data-block-id]');
      const root = event.target.closest(ROOT_SELECTOR);
      if (!block || !root?.wiCommunityPress) return;
      const index = Number(block.dataset.index);
      if (Number.isInteger(index)) root.wiCommunityPress.goTo(index, false);
    });
  }
})();
